import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FounderBlock from "../components/FounderBlock";
import SEOHead from "../components/SEOHead";
import RichTextRenderer from "../components/RichTextRenderer";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { getSeoPage, getPublicPath } from "../utils/seoPages";
import { ArrowRight, BookOpen, Users, Sparkles, Award, MessageCircle, Handshake, CheckCircle2, Star, CalendarCheck } from "lucide-react";
import { motion } from "framer-motion";

const LOGO = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654890/sf-learning/brand/logo_sf_learning.png";
const HERO_IMG = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654893/sf-learning/landing/hero_landing.png";
const COMMUNITY_IMG = "https://res.cloudinary.com/drzrhdf7c/image/upload/v1780654897/sf-learning/landing/illu_community.png";
const TESTIMONIAL_AVATARS = [
  "https://images.unsplash.com/photo-1760574740271-55e6683afe76?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzU2NDU3M3ww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1758272133833-3a2277d426e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwZGl2ZXJzZSUyMHBlb3BsZSUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NzU2NDU3M3ww&ixlib=rb-4.1.0&q=85",
];

function CourseCard({ c, refCode }) {
  const link = refCode ? `/formations/${c.slug || c.course_id}?ref=${refCode}` : `/formations/${c.slug || c.course_id}`;
  const totalLessons = (c.modules || []).reduce((sum, m) => sum + (m.lessons || []).length, 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="card overflow-hidden group"
      data-testid={`course-card-${c.course_id}`}
    >
      <Link to={link}>
        <div className="aspect-[16/10] bg-emerald-50 overflow-hidden">
          {c.cover_image ? (
            <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-100 to-amber-100 flex items-center justify-center">
              <BookOpen className="w-16 h-16 text-brand-green/60" />
            </div>
          )}
        </div>
      </Link>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          {c.category && <span className="badge orange">{c.category}</span>}
          <span className="badge gray">{totalLessons} leçons</span>
        </div>
        <Link to={link}>
          <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-brand-green transition-colors">{c.title}</h3>
        </Link>
        <RichTextRenderer html={c.short_description} className="text-sm text-gray-600 line-clamp-2" />
        <div className="mt-5 flex items-center justify-between">
          <div>
            <span className="text-xs text-gray-500">À partir de</span>
            <p className="text-2xl font-heading font-bold text-brand-green">{c.price_eur}€</p>
          </div>
          <Link to={link} className="btn-primary !px-4 !py-2 text-sm" data-testid={`course-card-cta-${c.course_id}`}>
            Découvrir <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
}

export default function Landing() {
  const [courses, setCourses] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [refCode, setRefCode] = useState("");
  const { settings: s } = useSettings();
  const heroImageSrc = s.hero_image || LOGO;
  const hasCustomHeroImage = Boolean(s.hero_image);
  const heroImagePosition = s.hero_image_position || "center center";
  const seo = getSeoPage(s, "/");
  const catalogPath = getPublicPath(s, "/catalog");
  const affiliatePath = getPublicPath(s, "/affiliate");
  const diagnosticPath = getPublicPath(s, "/diagnostic-strategique");

  useEffect(() => {
    api.post("/tracking/event", { event: "home_page_view", path: "/" }).catch(() => {});
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/testimonials/public").then((r) => setTestimonials(r.data)).catch(() => {});
    // capture referral code if present
    const u = new URLSearchParams(window.location.search);
    const ref = u.get("ref");
    if (ref) {
      setRefCode(ref);
      localStorage.setItem("sf_ref", ref);
      api.post("/affiliate/track-click", { code: ref, ua: navigator.userAgent, referrer: document.referrer }).catch(() => {});
    } else {
      const stored = localStorage.getItem("sf_ref");
      if (stored) setRefCode(stored);
    }
  }, []);

  return (
    <div data-testid="landing-page">
      <SEOHead title={seo.title} description={seo.description} image={heroImageSrc} url={typeof window !== "undefined" ? window.location.href : ""} type="website" />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="absolute right-[-200px] top-[-200px] w-[600px] h-[600px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(244,161,0,0.25), transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
              <span className="badge mb-5"><Sparkles className="w-3.5 h-3.5" /> {s.hero_badge}</span>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05]">
                {s.hero_title_line1}
                <br />
                <span className="text-brand-green">{s.hero_title_line2}</span>
                <br />
                <span className="relative inline-block">
                  {s.hero_title_line3}
                  <span className="absolute -bottom-2 left-0 right-0 h-3 bg-brand-orange/40 -z-10 rounded" />
                </span>
              </h1>
              <RichTextRenderer html={s.hero_subtitle} className="mt-7 text-lg text-gray-700 max-w-xl" />
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to={catalogPath} className="btn-primary" data-testid="hero-cta-catalog">
                  {s.hero_cta_primary} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to={diagnosticPath}
                  className="btn-orange"
                  data-testid="hero-cta-diagnostic"
                  onClick={() => api.post("/tracking/event", { event: "diagnostic_cta_click", path: "/", metadata: { source: "hero" } }).catch(() => {})}
                >
                  <CalendarCheck className="w-4 h-4" /> Ton diagnostic stratégique offert !
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {TESTIMONIAL_AVATARS.map((a, i) => (
                    <img key={i} src={a} alt="Apprenant" className="w-9 h-9 rounded-full border-2 border-brand-mint object-cover" />
                  ))}
                  <div className="avatar !w-9 !h-9 border-2 border-brand-mint">+1k</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-amber-500">
                    {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                  </div>
                  <p className="text-sm text-gray-600">{s.hero_social_proof}</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
              <div className="relative aspect-square max-w-[560px] mx-auto">
                {!hasCustomHeroImage && (
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{ background: "radial-gradient(closest-side, rgba(244,161,0,0.45), rgba(244,161,0,0.05) 70%)" }}
                  />
                )}
                <img
                  src={heroImageSrc}
                  alt={hasCustomHeroImage ? "Photo de présentation SF Learning" : "SF Learning Lotus"}
                  className={`absolute inset-0 m-auto w-3/4 h-3/4 drop-shadow-xl ${hasCustomHeroImage ? "rounded-[2rem] object-cover shadow-elev" : "object-contain"}`}
                  style={hasCustomHeroImage ? { objectPosition: heroImagePosition } : undefined}
                />
              </div>
              <div className="absolute top-8 -left-2 bg-white rounded-2xl shadow-elev p-4 max-w-[200px]">
                <p className="text-xs text-gray-500">{s.stat_community_label}</p>
                <p className="font-bold text-lg">{s.stat_community_value}</p>
              </div>
              <div className="absolute bottom-4 -right-4 bg-white rounded-2xl shadow-elev p-4 max-w-[220px]">
                <p className="text-xs text-gray-500">{s.stat_assignments_label}</p>
                <p className="font-bold text-lg">{s.stat_assignments_value}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: BookOpen, title: s.feature1_title, text: s.feature1_text },
            { icon: Award, title: s.feature2_title, text: s.feature2_text },
            { icon: Users, title: s.feature3_title, text: s.feature3_text },
          ].map(({ icon: Icon, title, text }, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="card p-6">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-brand-green" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">{title}</h3>
              <RichTextRenderer html={text} className="text-gray-600 text-sm" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Catalog preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <p className="label mb-2">{s.catalog_label}</p>
            <h2 className="font-heading text-4xl font-black">{s.catalog_title_line1} <span className="text-brand-green">{s.catalog_title_line2}</span></h2>
          </div>
          <Link to={catalogPath} className="btn-ghost" data-testid="see-all-courses-btn">
            Voir tout <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((c) => <CourseCard key={c.course_id} c={c} refCode={refCode} />)}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="home-testimonials-section">
          <div className="text-center mb-10">
            <p className="label text-brand-green">Avis apprenants</p>
            <h2 className="font-heading text-4xl font-black mt-2">Ils ont avancé avec <span className="text-brand-green">SF Learning</span></h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">Des retours vérifiés après validation, issus des parcours de formation.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((item) => (
              <div key={item.testimonial_id} className="card p-6 bg-white">
                <div className="flex items-center gap-1 text-amber-500 mb-3">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <Star key={rating} className={`w-4 h-4 ${item.rating >= rating ? "fill-current" : "text-gray-200"}`} />
                  ))}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">“{item.text}”</p>
                <div className="mt-5 flex items-center gap-3">
                  {item.user_picture ? <img src={item.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="avatar !w-10 !h-10">{(item.user_name || "A").slice(0,1).toUpperCase()}</div>}
                  <div>
                    <p className="font-semibold text-sm">{item.user_name || "Apprenant SF Learning"}</p>
                    <p className="text-xs text-gray-500">{item.course_title}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Affiliate banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card p-10 md:p-14 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-[300px] h-[300px] -mr-20 -mt-20 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(244,161,0,0.35), transparent 70%)" }} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center relative">
            <div>
              <p className="label mb-2 text-brand-orange">{s.affiliate_label}</p>
              <h2 className="font-heading text-4xl font-black mb-4">{s.affiliate_title_line1} <span className="text-brand-green">{s.affiliate_title_line2}</span></h2>
              <RichTextRenderer html={s.affiliate_subtitle} className="text-gray-700 mb-6" />
              <ul className="space-y-2 mb-8">
                {["Inscription gratuite & instantanée", "Lien + code de parrainage uniques", "Tableau de bord clair (clics, ventes, commissions)", "Paiement à la demande"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-5 h-5 text-brand-green mt-0.5 flex-shrink-0" /> {t}
                  </li>
                ))}
              </ul>
              <Link to={affiliatePath} className="btn-orange" data-testid="affiliate-banner-cta">
                <Handshake className="w-4 h-4" /> {s.affiliate_cta}
              </Link>
            </div>
            <div className="relative">
              <img src={COMMUNITY_IMG} alt="Communauté" className="rounded-2xl w-full" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Founder presentation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card p-10 bg-gradient-to-br from-emerald-50 to-amber-50">
          <FounderBlock />
        </div>
      </section>

      {/* Community CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="card p-10 text-center bg-gradient-to-br from-emerald-50 to-amber-50">
          <MessageCircle className="w-12 h-12 text-brand-green mx-auto mb-4" />
          <h2 className="font-heading text-3xl font-black mb-3">{s.community_title}</h2>
          <RichTextRenderer html={s.community_subtitle} className="text-gray-700 max-w-2xl mx-auto mb-6" />
          <Link to="/community" className="btn-primary" data-testid="community-cta">
            <Users className="w-4 h-4" /> {s.community_cta}
          </Link>
        </div>
      </section>
    </div>
  );
}
